import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  Eye, 
  Calendar, 
  Package, 
  User, 
  Tag,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Beaker,
  Clock,
  CheckCircle2,
  XCircle,
  FlaskConical
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Status badge colors
const STATUS_COLORS = {
  "Not Started": "bg-slate-100 text-slate-700 border-slate-200",
  "Started": "bg-blue-100 text-blue-700 border-blue-200",
  "End": "bg-amber-100 text-amber-700 border-amber-200",
  "Complete": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Finish": "bg-purple-100 text-purple-700 border-purple-200",
};

const STATUS_ICONS = {
  "Not Started": Clock,
  "Started": FlaskConical,
  "End": AlertTriangle,
  "Complete": CheckCircle2,
  "Finish": CheckCircle2,
};

export default function ProductDevelopmentReportPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [productTypeFilter, setProductTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/product-folders`);
      if (response.data.folders) {
        // Sort by created date (newest first)
        const sorted = response.data.folders.sort((a, b) => {
          const dateA = new Date(a.createdDateTime || a.portalCreatedAt || 0);
          const dateB = new Date(b.createdDateTime || b.portalCreatedAt || 0);
          return dateB - dateA;
        });
        setProducts(sorted);
      }
    } catch (error) {
      toast.error("Failed to load product development data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchProducts();
    setRefreshing(false);
    toast.success("Data refreshed successfully");
  };

  // Get unique product types for filter
  const productTypes = [...new Set(products.map(p => p.productType).filter(Boolean))];

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      (product.productName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (product.sampleNo?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (product.clientName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (product.sku?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || product.testStatus === statusFilter;
    const matchesType = productTypeFilter === "all" || product.productType === productTypeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const colorClass = STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-200";
    const StatusIcon = STATUS_ICONS[status] || Clock;
    return (
      <Badge className={`${colorClass} border font-poppins font-medium px-2.5 py-1 flex items-center gap-1.5`}>
        <StatusIcon className="w-3.5 h-3.5" />
        {status || "Not Started"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-vcc-cream-50 to-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-vcc-teal animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-poppins">Loading product development report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-poppins font-bold text-slate-800 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-vcc-teal to-vcc-teal/70 rounded-xl flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              Product Development Report
            </h1>
            <p className="text-slate-500 font-poppins mt-2 ml-15">
              View and analyze all product development records and iterations
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="font-poppins border-vcc-teal/30 text-vcc-teal hover:bg-vcc-teal/10"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">{products.length}</p>
              <p className="text-sm text-slate-500 font-poppins">Total Products</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">
                {products.filter(p => p.testStatus === "Started").length}
              </p>
              <p className="text-sm text-slate-500 font-poppins">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">
                {products.filter(p => p.testStatus === "Complete" || p.testStatus === "Finish").length}
              </p>
              <p className="text-sm text-slate-500 font-poppins">Completed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Beaker className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">
                {products.reduce((sum, p) => sum + (p.iterations?.length || 0), 0)}
              </p>
              <p className="text-sm text-slate-500 font-poppins">Total Iterations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-100 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by product name, sample no, client, or SKU..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 font-poppins"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[180px] font-poppins" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Not Started">Not Started</SelectItem>
              <SelectItem value="Started">Started</SelectItem>
              <SelectItem value="End">End</SelectItem>
              <SelectItem value="Complete">Complete</SelectItem>
              <SelectItem value="Finish">Finish</SelectItem>
            </SelectContent>
          </Select>

          <Select value={productTypeFilter} onValueChange={(v) => { setProductTypeFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[200px] font-poppins" data-testid="type-filter">
              <SelectValue placeholder="All Product Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Product Types</SelectItem>
              {productTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500 font-poppins">
          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="font-poppins font-semibold text-slate-700">Sample No</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Product Name</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Product Type</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">SKU</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Client Name</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Iterations</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Created Date</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <AlertTriangle className="w-10 h-10 text-slate-300" />
                    <p className="text-slate-500 font-poppins">No products found matching your criteria</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProducts.map((product) => (
                <TableRow 
                  key={product.id} 
                  className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/product-development-report/${product.id}`)}
                  data-testid={`product-row-${product.sampleNo}`}
                >
                  <TableCell className="font-poppins font-medium text-vcc-teal">
                    {product.sampleNo || "-"}
                  </TableCell>
                  <TableCell className="font-poppins font-medium text-slate-800 max-w-[200px] truncate">
                    {product.productName || "-"}
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    <Badge variant="outline" className="font-poppins bg-slate-50">
                      {product.productType || "-"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    {product.sku || "-"}
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {product.clientName || "-"}
                    </div>
                  </TableCell>
                  <TableCell className="font-poppins">
                    <Badge variant="secondary" className="bg-purple-50 text-purple-700 font-poppins">
                      {product.iterations?.length || 0} iterations
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(product.testStatus)}
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {formatDate(product.createdDateTime || product.portalCreatedAt)}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/product-development-report/${product.id}`);
                      }}
                      style={{ backgroundColor: '#2563eb', color: 'white' }}
                      className="hover:opacity-90 font-poppins shadow-sm"
                      data-testid={`view-report-${product.sampleNo}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Report
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="font-poppins"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className={`font-poppins w-9 ${currentPage === pageNum ? 'bg-vcc-teal' : ''}`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="font-poppins"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
